import type { Term, Tone } from "@/lib/campaign";
import { stageText } from "@/lib/contacts";
import { kindTerm, marksDoNotCall } from "@/lib/dispositions";
import type { TriggerInput, TriggerUpdateInput } from "@/lib/queries";
import {
  NO_ROUTING, ROUTING_OPTIONS, routingOptions, routingProblem, routingResultText, routingValues,
  type RoutingValues,
} from "@/lib/trigger-routing";
import type { Disposition, Trigger, TriggerInboxRow } from "@/lib/types";

/**
 * Triggers as the console shows and edits them (ADR-021): what each action does, where a request
 * stands and what it made, and the arithmetic between the form and core-api's `/triggers` routes.
 *
 * The rules are `platform`'s. The actions and the options each takes are `TRIGGER_ACTIONS` and
 * `_TRIGGER_OPTIONS`, a path is `trigger_mapping.PATH_RE`, the secret's header is
 * `_TRIGGER_HEADER_RE`, what an enlist needs is `_enlist_problem` and what a text needs is
 * `_message_problem` in core-api's `schemas.py`. The server decides all of it. The copies here keep
 * what it would refuse off the form and say why first. Pure, for `test/triggers.test.ts`.
 *
 * `message.received` (ADR-095) and `message.status` (ADR-098) are the two actions that are not about
 * a lead a request names: one keeps a text sent to one of our numbers, the other moves a text we sent
 * to delivered or failed. Their sender is a provider (Twilio), which takes the secret in the URL it
 * calls, as basic credentials, and reads the answer, so they have a `secret_kind` and a `reply` of
 * their own.
 *
 * `message.send` (platform ADR-131) sends a text for another system: to a lead, found as every lead
 * action finds one, or in a conversation, whose id a "Text received" webhook carries. Its `text` and
 * `from` are the words to send and the number of ours they leave from, so they have words of their
 * own here (`fieldTerm`). What it needs is `_send_problem`.
 *
 * `email.received` (platform ADR-180) keeps an email a contact sent to one of our reply addresses.
 * The Email settings make it, and SendGrid posts each email to its own route, `/hooks/t/<key>/email`,
 * which reads the email itself: it maps nothing, and needs only who posts the emails
 * (`_email_problem`). Its secret travels in the URL, the only way SendGrid sends one.
 */

// --- what a trigger does -------------------------------------------------------------------------

/** The order the form offers them in: what a zap needs most, first. */
export const TRIGGER_ACTIONS = [
  "lead.enlist", "lead.hold", "lead.release", "lead.delist", "lead.dnc", "lead.update", "lead.note",
  "lead.outcome", "message.send", "message.received", "message.status", "email.received", "capture",
] as const;

/** The actions that read the mapping's `fields` entry: a new lead's answers, or an existing lead's. */
export function fillsFields(action: string): boolean {
  return action === "lead.enlist" || action === "lead.update";
}

/** The action that sets an outcome on a lead (ADR-021 in platform, amended 2026-10-10). */
export const LEAD_OUTCOME = "lead.outcome";

/** The actions that write on the lead's own record, not on one campaign's dial list: no campaign
 *  narrows them. An outcome is one of them: it is set on the lead, and the lists follow it. */
export function writesRecord(action: string): boolean {
  return action === "lead.update" || action === "lead.note" || action === LEAD_OUTCOME;
}

/** The action that keeps a text (ADR-095). */
export const MESSAGE_RECEIVED = "message.received";
/** The action that takes a provider's report on a text we sent (ADR-098). */
export const MESSAGE_STATUS = "message.status";

/** The action that sends a text for another system (platform ADR-131). */
export const MESSAGE_SEND = "message.send";

/** The action that keeps an email a contact sent to one of our reply addresses (platform ADR-180). */
export const EMAIL_RECEIVED = "email.received";

/** Whether an action's sender is a text provider, not a system that names a lead. */
export function fromProvider(action: string): boolean {
  return action === MESSAGE_RECEIVED || action === MESSAGE_STATUS;
}

/** Whether a trigger is about one campaign's leads or every campaign's. Not the three about a text:
 *  a text's inbox is its number's, a report is about one text, and a text we are asked to send goes
 *  to the lead or the thread the request names. Nor an email: it joins the thread it answers. */
export function takesCampaign(action: string): boolean {
  return !fromProvider(action) && action !== MESSAGE_SEND && action !== EMAIL_RECEIVED;
}

const ACTIONS: Record<string, Term> = {
  "lead.enlist": {
    label: "Add to a dial list",
    description: "Puts the lead on the campaign's dial list, and makes the lead first when the number is new: a lead from a form, a CRM or a zap.",
  },
  "lead.hold": {
    label: "Hold the lead",
    description: "Keeps the lead off the dial lists until an appointment ends, or for a set number of hours.",
  },
  "lead.release": {
    label: "End the hold",
    description: "Ends a hold now, so the dialer may call the lead again: a no-show, a cancellation.",
  },
  "lead.delist": {
    label: "Take off the dial lists",
    description: "Takes the lead off their dial lists now: signed, or not a case after all.",
  },
  "lead.dnc": { label: "Do not call", description: "Marks the lead do-not-call, on every campaign." },
  "lead.update": {
    label: "Fill in the contact's fields",
    description:
      "Writes answers on the contact form of a lead the platform already has: a link another system made, the date something was signed. A blank value changes nothing.",
  },
  "lead.note": {
    label: "Add a note",
    description: "Puts a note on the lead's record, as the sender wrote it.",
  },
  "lead.outcome": {
    label: "Set an outcome",
    description:
      "Sets one of the outcomes ticked here on a lead the platform already has. It does what an outcome set on the record does: the stage, the dial lists, a hold, a do-not-call.",
  },
  "message.send": {
    label: "Send a text",
    description:
      "Sends a text from one of our numbers for another system: to a lead, or as an answer in a conversation. It gets every check a rep's text gets.",
  },
  "message.received": {
    label: "Receive a text",
    description:
      "Keeps a text sent to one of our numbers and puts it in the inbox the number names: the campaign's, a rep's own, or Unassigned.",
  },
  "message.status": {
    label: "Text delivery reports",
    description:
      "Takes the provider's report on a text we sent, and marks the text delivered, or failed and why.",
  },
  "email.received": {
    label: "Receive an email",
    description:
      "Keeps an email a contact sent to one of our reply addresses, in the thread it answers, or in Unassigned.",
  },
  capture: {
    label: "Only keep what arrives",
    description: "Stores each request and does nothing else. Point a zap here first to see the fields it sends, then map them.",
  },
};

export function actionTerm(action: string): Term {
  return ACTIONS[action] ?? { label: action, description: "An action this console has no words for yet." };
}

/** Which leads or texts a trigger touches, for the list and the page: the one campaign it names,
 *  every campaign, nothing (a capture), or whichever inbox a text's number names. */
export function scopeText(action: string, campaign: string | null): string {
  if (action === "capture") return "—";
  if (action === MESSAGE_RECEIVED) return "The number's inbox";
  if (action === MESSAGE_STATUS) return "The text it names";
  if (action === EMAIL_RECEIVED) return "The thread it answers";
  if (writesRecord(action)) return "The lead's own record";
  if (action === MESSAGE_SEND) return "The lead or conversation it names";
  return campaign ?? "Every campaign";
}

/** The options each action takes: `_TRIGGER_OPTIONS`. */
export const ACTION_OPTIONS: Readonly<Record<string, readonly string[]>> = {
  capture: [],
  "lead.hold": ["hours", "reason", "timezone"],
  "lead.release": ["reason"],
  "lead.delist": [],
  "lead.dnc": [],
  "lead.enlist": ["consent_wording", "list_name", "timezone", "require_consent", ...ROUTING_OPTIONS],
  "lead.update": [],
  "lead.note": [],
  "lead.outcome": ["outcomes", "timezone"],
  "message.received": ["provider", "reply"],
  "message.status": ["provider", "reply"],
  "message.send": [],
  "email.received": ["provider"],
};

/** The values a mapping can name, in the order the form asks for them. */
export const MAPPING_FIELDS = [
  "phone", "source_ref", "lead_id", "until", "first_name", "last_name", "language", "state", "timezone",
  "consent_at", "tags", "firm_id", "from", "to", "text", "message_id", "media_count", "status", "error_code", "note",
  "outcome", "callback_at",
  "conversation_id",
] as const;
export type MappingField = (typeof MAPPING_FIELDS)[number];

/** The fields that find a lead: `trigger_mapping.IDENTIFIERS`. */
export const IDENTIFIERS: readonly MappingField[] = ["lead_id", "source_ref", "phone"];
/** What a text is made of: `trigger_mapping.MESSAGE_FIELDS`. */
export const MESSAGE_FIELDS: readonly MappingField[] = ["from", "to", "text", "message_id", "media_count"];
/** The ones a text trigger's mapping has to name: `trigger_mapping.MESSAGE_REQUIRED`. */
export const MESSAGE_REQUIRED: readonly MappingField[] = ["from", "to", "text", "message_id"];
/** What a report on a text we sent is made of: `trigger_mapping.STATUS_FIELDS`. */
export const STATUS_FIELDS: readonly MappingField[] = ["message_id", "status", "error_code"];
/** The ones a report trigger's mapping has to name: `trigger_mapping.STATUS_REQUIRED`. */
export const STATUS_REQUIRED: readonly MappingField[] = ["message_id", "status"];
/** What a text we are asked to send is made of: `trigger_mapping.SEND_FIELDS`, and who it is to. */
export const SEND_FIELDS: readonly MappingField[] = ["text", "from", "conversation_id", ...IDENTIFIERS];

const ACTION_FIELDS: Readonly<Record<string, readonly MappingField[]>> = {
  capture: [],
  "lead.hold": [...IDENTIFIERS, "until"],
  "lead.release": IDENTIFIERS,
  "lead.delist": IDENTIFIERS,
  "lead.dnc": IDENTIFIERS,
  "lead.enlist": [
    ...IDENTIFIERS, "first_name", "last_name", "language", "state", "timezone", "consent_at", "tags", "firm_id",
  ],
  "lead.update": IDENTIFIERS,
  "lead.note": [...IDENTIFIERS, "note"],
  "lead.outcome": [...IDENTIFIERS, "outcome", "callback_at", "note"],
  "message.received": MESSAGE_FIELDS,
  "message.status": STATUS_FIELDS,
  "message.send": SEND_FIELDS,
  // The email route reads the email itself: nothing is mapped.
  "email.received": [],
};

/** The fields an action reads. A field only another action reads is not sent. */
export function actionFields(action: string): readonly MappingField[] {
  return ACTION_FIELDS[action] ?? IDENTIFIERS;
}

export const FIELD_WORDS: Record<MappingField, Term> = {
  phone: { label: "Phone number", description: "The lead's number, as a US number in any common format, or E.164." },
  source_ref: {
    label: "Sender's id",
    description: "The sending system's own id for the lead: a GHL contact id, a form entry's id.",
  },
  lead_id: { label: "Our lead id", description: "The platform's id for the lead, when the sender got it from a webhook." },
  until: {
    label: "Hold until",
    description: "When the hold ends, like the appointment's end: ISO 8601, epoch seconds, or MM/DD/YYYY with a time.",
  },
  first_name: { label: "First name", description: "" },
  last_name: { label: "Last name", description: "" },
  language: { label: "Language", description: "en or es, or the language's name: English, Spanish." },
  state: { label: "State", description: "The two-letter state, like CA." },
  timezone: {
    label: "Lead's time zone",
    description: "An IANA zone, like America/Los_Angeles. The lead's quiet hours are read in it.",
  },
  consent_at: { label: "Agreed at", description: "When the lead agreed to be called. Not in the future." },
  tags: {
    label: "Tags",
    description:
      "The sender's tags, as a list or as one comma-separated field. The firm, the language and the disposition are read from them.",
  },
  firm_id: {
    label: "Firm id",
    description:
      "The firm a new lead goes to, by its id on the Firms page, as the campaign is chosen by its id. Read only with a firm split; when it is missing or not one of yours, the split decides.",
  },
  from: { label: "Sender", description: "The number the text came from." },
  to: { label: "Our number", description: "The number of ours it was sent to. It decides the inbox." },
  text: { label: "The text", description: "What it says." },
  message_id: {
    label: "Provider's id",
    description:
      "The provider's own id for the text. The same text delivered twice is kept once, and a report finds its text by it.",
  },
  media_count: { label: "Pictures", description: "How many pictures came with it, when the provider says." },
  status: {
    label: "What became of it",
    description: "The provider's word for where the text stands: sent, delivered, undelivered, failed.",
  },
  error_code: { label: "Why it failed", description: "The provider's code for a text that failed, when it sends one." },
  note: {
    label: "The note",
    description: "What to write on the lead's record. A note is at most 4,000 characters; the rest is cut.",
  },
  outcome: {
    label: "The outcome",
    description: "Which of the ticked outcomes a request sets: its slug, like contract_sent, or its name as it is listed here.",
  },
  callback_at: {
    label: "Callback time",
    description:
      "When a Callback is, as a rep would pick it: ISO 8601 with its offset, epoch seconds, or MM/DD/YYYY with a time. Not in the past, at most 90 days ahead. Left empty, the dialer's own wait.",
  },
  conversation_id: {
    label: "Conversation",
    description: "The thread to answer: the conversation id a Text received webhook carries.",
  },
};

/** A send trigger's own words for the two fields it shares with a text that arrived. */
const SEND_WORDS: Partial<Record<MappingField, Term>> = {
  text: { label: "The text", description: "The words to send." },
  from: {
    label: "Our number",
    description:
      "Which of our numbers it leaves from. Left empty, an answer leaves from its thread's number and a text to a lead from their campaign's.",
  },
};

/** A mapping field in the words of the action that reads it. */
export function fieldTerm(action: string, field: MappingField): Term {
  return (action === MESSAGE_SEND ? SEND_WORDS[field] : undefined) ?? FIELD_WORDS[field];
}

// --- a request -----------------------------------------------------------------------------------

export const INBOX_STATUSES = ["received", "applied", "rejected", "failed"] as const;

const INBOX: Record<string, Term & { tone: Tone }> = {
  received: { label: "Waiting", description: "Stored, and applied within seconds.", tone: "neutral" },
  applied: { label: "Done", description: "The action ran.", tone: "success" },
  rejected: {
    label: "Not applied",
    description: "The action could not run on what the request carried, and says why.",
    tone: "warning",
  },
  failed: {
    label: "Failed",
    description: "The action raised an error five times. Send it again once the cause is fixed.",
    tone: "danger",
  },
};

export function inboxStatusText(status: string): Term & { tone: Tone } {
  return INBOX[status] ?? { label: status, description: "Not a status this console knows.", tone: "outline" };
}

/** Why a request was not applied: the `reason`s `trigger_actions.py` writes. */
const REASONS: Record<string, string> = {
  no_lead: "No lead has the number or id this request carries.",
  bad_until: "The end time could not be read.",
  no_phone: "No phone number for a new lead.",
  bad_phone: "The phone number is not one the platform can call.",
  no_source_ref: "No id from the sender for a new lead.",
  no_consent: "No moment of consent for a new lead.",
  bad_consent_date: "The moment of consent could not be read, or is in the future.",
  dnc: "The lead is on the do-not-call list.",
  no_campaign: "The trigger's campaign is gone.",
  unknown_action: "The trigger's action is not one the platform knows.",
  no_from: "The request names no sender where the mapping looks for one.",
  no_to: "The request names none of our numbers where the mapping looks for one.",
  no_message_id: "The request carries no id for the text where the mapping looks for one.",
  no_text: "The text has no words and no picture.",
  bad_from: "The sender is neither a phone number nor a short code or name.",
  bad_to: "The number it was sent to is not a phone number the platform can read.",
  not_our_number: "The number it was sent to belongs to another tenant.",
  no_status: "The report does not say what became of the text where the mapping looks for it.",
  unknown_text: "No text of ours has the id this report names.",
  no_fields: "The trigger maps no field of the contact's form.",
  nothing_stored: "None of the values this request carried could be kept in their fields.",
  no_note: "The request carries no note where the mapping looks for one.",
  no_outcome: "The request names no outcome where the mapping looks for one.",
  outcome_not_listed: "The request names an outcome this trigger may not set.",
  not_offered: "The lead's campaign does not offer this outcome.",
  needs_note: "This outcome needs a note, and the request carries none.",
  exit: "The lead is in an end stage, and this outcome would take them out of it.",
  bad_callback_at: "The callback time could not be read.",
  callback_at: "The callback time was refused: in the past, more than 90 days ahead, or on an outcome that is not a callback.",
};

/** Why an outcome was not set, with which outcome and which stage: what an admin fixes the trigger,
 *  or the sender, from. The name a sender used is shown as it came. */
function outcomeRefusal(reason: string, result: Record<string, unknown>): string | null {
  const outcome = typeof result.outcome === "string" && result.outcome ? result.outcome : null;
  if (!outcome) return null;
  switch (reason) {
    case "outcome_not_listed":
      return `The request names "${outcome}", which this trigger may not set.`;
    case "not_offered":
      return `The lead's campaign does not offer ${outcome}.`;
    case "needs_note":
      return `${outcome} needs a note, and the request carries none.`;
    case "callback_at":
      // The platform's own words, as the console says them to a rep picking a time.
      return typeof result.detail === "string" && result.detail ? `${result.detail} (${outcome}).` : null;
    case "exit": {
      const stage = typeof result.stage === "string" ? ` (${stageText(result.stage).label})` : "";
      return `The lead is in an end stage${stage}, and ${outcome} would take them out of it.`;
    }
    default:
      return null;
  }
}

/** Why a text we were asked to send did not leave: `trigger_actions._message_send`'s own reasons,
 *  and the refusals a rep's text gets (`messages.py`). Read before `REASONS`, where three of these
 *  codes mean something else. */
const SEND_REASONS: Record<string, string> = {
  no_text: "The request carries no words where the mapping looks for the text.",
  no_lead: "No lead has the number or id this request carries.",
  no_conversation: "No conversation of this tenant has the id this request carries.",
  no_number: "The lead has no number of ours to be texted from: no campaign, and no thread yet.",
  not_our_number: "The number to send from belongs to another tenant.",
  not_the_threads_number: "An answer leaves from the number its conversation is on, and the request names another.",
  bad_from: "The number to send from is not a phone number.",
  not_set_up: "Sending texts is switched off. An admin switches it on in Settings, SMS.",
  not_a_phone: "The person's number is not a phone number a text can go to.",
  dnc: "The person is on the do-not-call list.",
  opted_out: "The person texted STOP.",
  no_consent: "The lead has not agreed to be contacted, and has not texted us.",
  number_cannot_text: "The number it would leave from is not set up to send texts.",
  no_timezone: "Nothing says where the person is, so their 8 am to 9 pm cannot be worked out.",
  daily_limit: "The day's limit of texts has been reached.",
  too_long: "The text is longer than 1,600 characters.",
};

/** Why an email that reached us was not kept: `trigger_actions._email_received` and `emails.receive`.
 *  Read before `REASONS`, where three of these codes mean something else. */
const EMAIL_REASONS: Record<string, string> = {
  too_large: "The email was larger than 32 MB, so it was not read.",
  not_multipart: "The request was not a form, so it was not read as an email.",
  too_many_parts: "The request had more parts than an email has, so it was not read.",
  unreadable: "The request could not be read as an email.",
  raw_mime: "SendGrid sends the raw message: turn off 'POST the raw, full MIME message'.",
  no_to: "The email names no address it was sent to.",
  no_from: "The email names no sender we can read.",
  not_set_up: "The Email settings have no reply domain.",
  not_our_domain: "It was sent to no address at this tenant's reply domain.",
  from_us: "It came from our own from address or reply domain.",
  our_own: "It is an email of ours, come back.",
  no_thread: "It was sent to an address at the reply domain that is not a reply address.",
  spam: "It was sent to an unknown reply address, and SendGrid scored it as spam.",
  unassigned_cap: "Today's new Unassigned email threads are all made.",
  ambiguous: "It was sent to two contacts' reply addresses by a sender whose address already holds both in Unassigned.",
};

/** How an email's thread was found (`emails.receive`), as a result says it. */
const MATCHED_WORDS: Record<string, string> = {
  reply_address: " (matched by its reply address)",
  in_reply_to: " (matched as a reply)",
  references: " (matched as a reply)",
  unassigned: ", with no contact",
  ambiguous: ", with no contact: it named two contacts' reply addresses",
};

/** Where a text we sent stands, as a report's result says it (`messaging.MESSAGE_STATUSES`). */
const STATUS_WORDS: Record<string, string> = {
  queued: "waiting to be sent",
  sent: "sent",
  delivered: "delivered",
  failed: "not delivered",
};

/** The inbox a text landed in (`messaging.INBOX_KINDS`), as a result says it. */
const INBOX_WORDS: Record<string, string> = {
  campaign: "the campaign's inbox",
  rep: "the rep's own inbox",
  unassigned: "Unassigned",
};

/** What a lead's form answers came to (`trigger_actions._fill_fields`): field keys only, never a
 *  value. Empty when the trigger fills none. Without `count` it leaves out how many were filled, for
 *  a request that filled none and already says so. */
function formFieldsText(fields: unknown, count = true): string {
  if (!fields || typeof fields !== "object") return "";
  const of = (key: string): string[] => {
    const list = (fields as Record<string, unknown>)[key];
    if (Array.isArray(list)) return list.filter((k): k is string => typeof k === "string");
    if (list && typeof list === "object") return Object.keys(list);
    return [];
  };
  const stored = of("stored");
  const skipped = of("skipped");
  const missing = of("missing");
  const ambiguous = of("ambiguous");
  let out = count ? ` Filled ${plural(stored.length, "form field")}.` : "";
  if (skipped.length) out += ` Skipped ${skipped.join(", ")}.`;
  if (missing.length) out += ` Not in the request: ${missing.join(", ")}.`;
  if (ambiguous.length) out += ` Named twice in the request: ${ambiguous.join(", ")}.`;
  return out;
}

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

function sentence(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/** What a request made, in one line. `when` writes the moment a hold ends. */
export function resultText(
  row: Pick<TriggerInboxRow, "status" | "result" | "error">,
  action: string,
  when: (iso: string) => string = (iso) => iso,
): string {
  const result: Record<string, unknown> = row.result ?? {};
  if (row.status === "received") return "Waiting to be applied.";
  if (row.status === "failed") return row.error ? `Failed: ${row.error}` : "Failed.";
  if (row.status === "rejected") {
    const reason = typeof result.reason === "string" ? result.reason : "";
    if (action === MESSAGE_SEND) {
      if (reason === "quiet_hours") {
        const until = typeof result.until === "string" ? ` Their morning comes at ${when(result.until)}.` : "";
        return `Not sent: it is outside the person's 8 am to 9 pm.${until}`;
      }
      if (SEND_REASONS[reason]) return `Not sent. ${SEND_REASONS[reason]}`;
    }
    if (action === EMAIL_RECEIVED && EMAIL_REASONS[reason]) return EMAIL_REASONS[reason];
    // Which fields, and what the request did not carry: what an admin fixes the mapping from.
    if (reason === "nothing_stored") return REASONS[reason] + formFieldsText(result.fields, false);
    return outcomeRefusal(reason, result) ?? REASONS[reason] ?? (reason ? `Not applied: ${reason}.` : "Not applied.");
  }
  const note = typeof result.note === "string" ? result.note : null;
  const count = (key: string) => (Array.isArray(result[key]) ? (result[key] as unknown[]).length : 0);
  switch (action) {
    case "capture":
      return "Kept, and nothing else.";
    case "lead.hold": {
      if (note) return sentence(note);
      const until = typeof result.hold_until === "string" ? when(result.hold_until) : "the end";
      return `Held on ${plural(count("campaign_leads"), "dial list")} until ${until}.`;
    }
    case "lead.release":
      return count("released") > 0
        ? `Ended the hold on ${plural(count("released"), "dial list")}.`
        : sentence(note ?? "nothing was held");
    case "lead.delist":
      return count("retired") > 0
        ? `Took the lead off ${plural(count("retired"), "dial list")}.`
        : sentence(note ?? "already off every dial list");
    case "lead.dnc":
      return result.dnc === "already" ? "Already do-not-call." : "Marked do-not-call.";
    case "lead.enlist": {
      const made =
        result.created === true
          ? "Made a new lead, and put them on the dial list."
          : note
            ? sentence(note)
            : "Put a lead the platform had on the dial list.";
      return made + routingResultText(result) + formFieldsText(result.fields);
    }
    case "lead.update":
      // For a request that carried nothing, the server's own sentence, then which names it lacked.
      return note ? sentence(note) + formFieldsText(result.fields, false) : formFieldsText(result.fields).trim() || "Applied.";
    case "lead.note":
      return note ? `Added a note. ${sentence(note)}` : "Added a note.";
    case LEAD_OUTCOME: {
      // By slug and by id, as the result has them: the outcome, then everything it did.
      const outcome = typeof result.outcome === "string" ? result.outcome : "the outcome";
      const stage = typeof result.stage === "string" ? stageText(result.stage).label : null;
      let out = `Set ${outcome}.`;
      out += result.stage_moved === true && stage ? ` The lead moved to ${stage}.` : " The stage did not move.";
      if (result.dnc === "set") out += " Marked do-not-call.";
      if (result.dnc === "already") out += " The lead was already do-not-call.";
      if (count("held") > 0) out += ` Held on ${plural(count("held"), "dial list")}.`;
      if (typeof result.handed_to_campaign_id === "string") out += " Handed to the campaign the outcome names.";
      if (typeof result.callback_at === "string") out += ` A callback is booked for ${when(result.callback_at)}.`;
      if (typeof result.note_id === "number") out += " Its note was kept.";
      return note ? `${out} ${sentence(note)}` : out;
    }
    case "message.received": {
      if (note) return sentence(note);
      const inbox = typeof result.inbox === "string" ? result.inbox : "";
      const where = INBOX_WORDS[inbox] ?? "an inbox";
      const who = typeof result.lead_id === "string" ? "" : ", from a number with no contact";
      const stop =
        result.dnc === "set"
          ? " It was a STOP: the lead is now do-not-call."
          : result.opt_out === true
            ? " It was a STOP."
            : "";
      return `Kept in ${where}${who}.${stop}`;
    }
    case "email.received": {
      if (note) return sentence(note);
      const inbox = typeof result.inbox === "string" ? result.inbox : "";
      const matched = typeof result.matched_by === "string" ? (MATCHED_WORDS[result.matched_by] ?? "") : "";
      let out = `Kept in ${INBOX_WORDS[inbox] ?? "an inbox"}${matched}.`;
      if (result.automatic === true) out += " An automatic reply.";
      const dropped = typeof result.attachments_dropped === "number" ? result.attachments_dropped : 0;
      if (dropped > 0) out += ` ${dropped === 1 ? "1 attachment was" : `${dropped} attachments were`} not kept.`;
      return out;
    }
    case "message.status": {
      const status = typeof result.status === "string" ? result.status : "";
      const stands = `The text is ${STATUS_WORDS[status] ?? (status || "as it was")}.`;
      return note ? `Nothing changed: ${note}. ${stands}` : stands;
    }
    case "message.send": {
      const status = typeof result.text_status === "string" ? result.text_status : "";
      const error = typeof result.error === "string" ? ` (${result.error})` : "";
      const stands =
        status === "sent" || status === "delivered"
          ? "Sent the text."
          : status === "failed"
            ? `The text was kept, and the provider did not take it${error}.`
            : status === "queued"
              ? "The text is kept and has not left yet."
              : status === "scheduled"
                ? // Outside the person's hours (ACL-630): it goes at their 8 am.
                  `The text is kept and goes out ${typeof result.send_at === "string" ? when(result.send_at) : "later"}, the person's 8 am.`
                : "Kept the text.";
      return note ? `${sentence(note)} ${stands}` : stands;
    }
    default:
      return "Applied.";
  }
}

// --- the form ------------------------------------------------------------------------------------

export const DEFAULT_SECRET_HEADER = "X-ACL-Trigger-Secret";
/** `trigger_auth.SECRET_KINDS`: the secret in a header, or as the password of basic credentials. */
export const SECRET_KINDS = ["header", "basic"] as const;

const SECRET_KIND: Record<string, Term> = {
  header: {
    label: "In a header",
    description: "The sender adds a header with the secret to every request. A zap and a GHL workflow can.",
  },
  basic: {
    label: "In the URL, as a password",
    description:
      "For a sender that can set no header and takes credentials in the URL it calls. Twilio does.",
  },
};

export function secretKindText(kind: string): Term {
  return SECRET_KIND[kind] ?? { label: kind, description: "Not a way this console knows." };
}

/** `schemas.TRIGGER_REPLIES`: what a sender is answered. */
export const TRIGGER_REPLIES = ["json", "twiml"] as const;

const REPLY: Record<string, Term> = {
  json: { label: "JSON", description: "What every trigger answers: the request's id and whether it is new." },
  twiml: {
    label: "Empty TwiML",
    description:
      "For Twilio, which reads the answer as instructions: it logs JSON as an error and texts a plain answer back to the sender.",
  },
};

export function replyText(reply: string): Term {
  return REPLY[reply] ?? { label: reply, description: "Not an answer this console knows." };
}

/** The user name in a URL's credentials. Only the secret is checked; this is what the console writes. */
export const BASIC_USER = "acl";
/** `campaign_id: null`: every campaign of the tenant. */
export const EVERY_CAMPAIGN = "";
export const MAX_ATTRIBUTES = 30;
export const MAX_IDEMPOTENCY_PATHS = 5;
export const MAX_HOLD_HOURS = 24 * 90;

const STEP = String.raw`[A-Za-z0-9_\-]+(?:\[\d{1,4}\])?`;
/** A dotted path into a request, as `trigger_mapping.PATH_RE`. */
export const PATH_RE = new RegExp(String.raw`^(?:\$\.)?${STEP}(?:\.${STEP}){0,15}$`);
const HEADER_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,99}$/;
/** Where basic credentials travel: `trigger_auth.BASIC_HEADER`. */
export const BASIC_HEADER = "Authorization";
const RESERVED_HEADERS = new Set([
  "authorization", "connection", "content-length", "content-type", "cookie", "host", "idempotency-key",
  "transfer-encoding", "x-forwarded-for", "x-service-token",
]);
const REASON_RE = /^[a-z0-9][a-z0-9_]{0,49}$/;
const PROVIDER_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const ATTRIBUTE_NAME_RE = /^[A-Za-z0-9_-]{1,64}$/;
const PLAIN_NAME_RE = /^[A-Za-z0-9_-]+$/;

export interface AttributeRow {
  name: string;
  path: string;
}

export interface TriggerValues {
  name: string;
  description: string;
  action: string;
  /** A campaign's id, or `EVERY_CAMPAIGN`. */
  campaign_id: string;
  lead_source: string;
  /** header | basic */
  secret_kind: string;
  secret_header: string;
  enabled: boolean;
  /** A path per field; blank for a field the request does not carry. */
  mapping: Record<MappingField, string>;
  attributes: AttributeRow[];
  idempotency: string[];
  /** The options, as typed. */
  hours: string;
  reason: string;
  timezone: string;
  /** Kept as loaded and never shown: consent is nobody's to set (ACL-575, platform ADR-194). */
  consent_wording: string;
  list_name: string;
  /** Kept as loaded and never shown; core-api reads it no more. */
  require_consent: boolean;
  /** A text trigger's provider, as each text keeps it: twilio. */
  provider: string;
  /** json | twiml */
  reply: string;
  /** The outcomes a `lead.outcome` trigger may set, by slug, in the catalog's order. */
  outcomes: string[];
  /** A lead's form answers and where each is in the request (ACL-375, ADR-119): a form field's key
   *  to a path, or to `{ name }`, a top-level key as the sender writes it (GHL's labels). A new
   *  lead's for `lead.enlist`, an existing lead's for `lead.update`. Edited in the form's field
   *  mapper (`FieldMapper`); kept exactly as they are when nothing there changes. */
  fields: Record<string, FieldSource>;
  /** A new-lead trigger's firm split, answer rules and extra tags (ADR-175 in platform). */
  routing: RoutingValues;
}

/** Where a form answer is in a request: a path, or a top-level key by its name. */
export type FieldSource = string | { name: string };

/** The `fields` entry as stored, cleaned: a source that is neither a path nor a name is left out. */
export function fieldSources(value: unknown): Record<string, FieldSource> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, FieldSource> = {};
  for (const [key, source] of Object.entries(value)) {
    if (typeof source === "string") out[key] = source;
    else if (source && typeof source === "object" && typeof (source as { name?: unknown }).name === "string") {
      out[key] = { name: (source as { name: string }).name };
    }
  }
  return out;
}

function blankMapping(): Record<MappingField, string> {
  return Object.fromEntries(MAPPING_FIELDS.map((field) => [field, ""])) as Record<MappingField, string>;
}

/** A new trigger starts as a zap's new-lead trigger, with its fields named the way "How to call it"
 *  tells a zap to name them. */
export const NEW_TRIGGER: TriggerValues = {
  name: "",
  description: "",
  action: "lead.enlist",
  campaign_id: EVERY_CAMPAIGN,
  lead_source: "zapier",
  secret_kind: "header",
  secret_header: DEFAULT_SECRET_HEADER,
  enabled: true,
  mapping: {
    ...blankMapping(),
    phone: "phone",
    source_ref: "id",
    first_name: "first_name",
    last_name: "last_name",
  },
  attributes: [],
  idempotency: [],
  hours: "",
  reason: "",
  timezone: "",
  consent_wording: "",
  list_name: "",
  require_consent: true,
  provider: "",
  reply: "json",
  outcomes: [],
  fields: {},
  routing: NO_ROUTING,
};

/** What Twilio sends and needs: its form's field names, the secret in the URL, an answer in TwiML. */
export const TWILIO_TEXTS = {
  provider: "twilio",
  reply: "twiml",
  secret_kind: "basic",
  mapping: { from: "From", to: "To", text: "Body", message_id: "MessageSid", media_count: "NumMedia" },
} as const;

/** Who posts the emails an email trigger keeps (platform ADR-180): SendGrid's Inbound Parse, with the
 *  secret in the URL. */
export const SENDGRID_EMAILS = { provider: "sendgrid", secret_kind: "basic" } as const;

/** What Twilio sends about a text we sent, and needs: its report's field names, and the secret in
 *  the URL. It does not read the answer to a report, so the answer stays JSON. */
export const TWILIO_REPORTS = {
  provider: "twilio",
  reply: "json",
  secret_kind: "basic",
  mapping: { message_id: "MessageSid", status: "MessageStatus", error_code: "ErrorCode" },
} as const;

/**
 * The form after its action is changed. A trigger turned into a text trigger, or a report trigger,
 * with nothing said yet about where a request carries its fields starts as Twilio's, which is who
 * delivers ours: the paths, the provider, the secret in the URL and the answer. Anything already
 * typed is kept.
 *
 * Leaving the provider's actions puts the secret back where the form was loaded with it (`loaded`).
 * The preset moved it into the URL for Twilio, and saved on a zap's or a GHL workflow's trigger that
 * would refuse every request the sender makes with its header, with nothing in Requests to say so.
 *
 * An email trigger is SendGrid's: who posts the emails, and the secret in the URL, the only place
 * SendGrid sends one. Leaving it takes both away again, so a text trigger made from it starts as
 * Twilio's and not as SendGrid's.
 */
export function withAction(
  current: TriggerValues,
  action: string,
  loaded: Pick<TriggerValues, "secret_kind"> = NEW_TRIGGER,
): TriggerValues {
  if (action === EMAIL_RECEIVED) {
    return { ...current, action, provider: current.provider.trim() || SENDGRID_EMAILS.provider, secret_kind: "basic" };
  }
  const values =
    current.action === EMAIL_RECEIVED
      ? { ...current, action: "", provider: "", secret_kind: loaded.secret_kind }
      : current;
  const next = { ...values, action };
  if (!fromProvider(action)) {
    return fromProvider(values.action) ? { ...next, secret_kind: loaded.secret_kind } : next;
  }
  const preset = action === MESSAGE_STATUS ? TWILIO_REPORTS : TWILIO_TEXTS;
  const fields = action === MESSAGE_STATUS ? STATUS_FIELDS : MESSAGE_FIELDS;
  // Coming from the provider's other action, the provider and the secret are already said, and the
  // text's id is mapped: what is new is only where this action's own fields are.
  const typed = fields.filter((field) => values.mapping[field].trim());
  const shared = fromProvider(values.action) ? ["message_id"] : [];
  const blank = typed.every((field) => shared.includes(field)) && (fromProvider(values.action) || !values.provider.trim());
  if (!blank) {
    // Twilio's own trigger, turned from one of its actions to the other with its paths already
    // there: what Twilio is answered follows the action. It reads the answer to a text as
    // instructions, and does not read the answer to a report.
    const twilios = fromProvider(values.action) && values.provider.trim() === preset.provider;
    return twilios ? { ...next, reply: preset.reply } : next;
  }
  const unsaid = Object.entries(preset.mapping).filter(([field]) => !values.mapping[field as MappingField].trim());
  return {
    ...next,
    provider: values.provider.trim() || preset.provider,
    reply: preset.reply,
    secret_kind: preset.secret_kind,
    mapping: { ...values.mapping, ...Object.fromEntries(unsaid) },
  };
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function triggerValues(trigger: Trigger): TriggerValues {
  const mapping = trigger.mapping ?? {};
  const options = trigger.options ?? {};
  const attributes = mapping.attributes;
  return {
    name: trigger.name,
    description: trigger.description ?? "",
    action: trigger.action,
    campaign_id: trigger.campaign_id ?? EVERY_CAMPAIGN,
    lead_source: trigger.lead_source ?? "",
    secret_kind: trigger.secret_kind || "header",
    // A basic trigger's header is Authorization, which is no header of its own: the form keeps the
    // default ready for the day it goes back to one.
    secret_header: trigger.secret_kind === "basic" ? DEFAULT_SECRET_HEADER : trigger.secret_header,
    enabled: trigger.enabled,
    mapping: Object.fromEntries(MAPPING_FIELDS.map((field) => [field, text(mapping[field])])) as Record<
      MappingField,
      string
    >,
    attributes:
      attributes && typeof attributes === "object" && !Array.isArray(attributes)
        ? Object.entries(attributes).map(([name, path]) => ({ name, path: text(path) }))
        : [],
    idempotency: Array.isArray(mapping.idempotency) ? mapping.idempotency.map(text) : [],
    hours: typeof options.hours === "number" ? String(options.hours) : "",
    reason: text(options.reason),
    timezone: text(options.timezone),
    consent_wording: text(options.consent_wording),
    list_name: text(options.list_name),
    require_consent: options.require_consent !== false,
    provider: text(options.provider),
    reply: text(options.reply) || "json",
    outcomes: Array.isArray(options.outcomes) ? options.outcomes.filter((slug): slug is string => typeof slug === "string") : [],
    fields: fieldSources(mapping.fields),
    routing: routingValues(options),
  };
}

function validZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** Why a trigger cannot be saved as it stands, or null: `trigger_settings_problem` and the field
 *  checks around it. */
export function triggerProblem(values: TriggerValues): string | null {
  if (!values.name.trim()) return "A trigger needs a name.";
  if (values.secret_kind !== "basic") {
    const header = values.secret_header.trim();
    if (!HEADER_RE.test(header)) return "The secret's header is a name of letters, digits and hyphens, up to 100.";
    if (RESERVED_HEADERS.has(header.toLowerCase())) {
      return `${header} already means something on a request; name another header.`;
    }
  }
  const reads = actionFields(values.action);
  for (const field of reads) {
    const path = values.mapping[field].trim();
    if (path && !PATH_RE.test(path)) {
      return `${fieldTerm(values.action, field).label}: "${path}" is not a path into the request. Write one like contact.phone.`;
    }
  }
  if (values.action === "lead.enlist") {
    const rows = values.attributes.filter((row) => row.name.trim() || row.path.trim());
    if (rows.length > MAX_ATTRIBUTES) return `A trigger fills at most ${MAX_ATTRIBUTES} attributes.`;
    const seen = new Set<string>();
    for (const row of rows) {
      if (!ATTRIBUTE_NAME_RE.test(row.name.trim())) {
        return "An attribute's name is letters, digits, _ and -, up to 64.";
      }
      if (!PATH_RE.test(row.path.trim())) return `The attribute ${row.name.trim()} needs a path into the request.`;
      if (seen.has(row.name.trim())) return `The attribute ${row.name.trim()} is named twice.`;
      seen.add(row.name.trim());
    }
  }
  const idempotency = values.idempotency.map((path) => path.trim()).filter(Boolean);
  if (idempotency.length > MAX_IDEMPOTENCY_PATHS) {
    return `A request is known by at most ${MAX_IDEMPOTENCY_PATHS} paths.`;
  }
  if (idempotency.some((path) => !PATH_RE.test(path))) {
    return "Each path a request is known by has to be a path into the request.";
  }
  const takes = ACTION_OPTIONS[values.action] ?? [];
  if (takes.includes("hours") && values.hours.trim()) {
    const hours = Number(values.hours);
    if (!Number.isInteger(hours) || hours < 1 || hours > MAX_HOLD_HOURS) {
      return `Hours is a whole number from 1 to ${MAX_HOLD_HOURS}.`;
    }
  }
  if (takes.includes("reason") && values.reason.trim() && !REASON_RE.test(values.reason.trim())) {
    return "A reason is lowercase letters, digits and underscores, up to 50.";
  }
  if (takes.includes("timezone") && values.timezone.trim() && !validZone(values.timezone.trim())) {
    return "A time zone is an IANA name, like America/Los_Angeles.";
  }
  if (values.action === "capture") return null;
  if (values.action === EMAIL_RECEIVED) {
    if (!values.provider.trim()) return "Say who posts the emails, like sendgrid.";
    if (!PROVIDER_RE.test(values.provider.trim())) {
      return "The provider is a name in lowercase letters, digits, _ and -, up to 40.";
    }
    return null;
  }
  const mapped = (field: MappingField) => values.mapping[field].trim() !== "";
  if (values.action === MESSAGE_RECEIVED) {
    const missing = MESSAGE_REQUIRED.filter((field) => !mapped(field));
    if (missing.length > 0) {
      return `Say where a request carries the text: ${missing.map((f) => FIELD_WORDS[f].label.toLowerCase()).join(", ")}.`;
    }
    if (!values.provider.trim()) return "Say who delivers the texts, like twilio.";
    if (!PROVIDER_RE.test(values.provider.trim())) {
      return "The provider is a name in lowercase letters, digits, _ and -, up to 40.";
    }
    return null;
  }
  if (values.action === MESSAGE_STATUS) {
    const missing = STATUS_REQUIRED.filter((field) => !mapped(field));
    if (missing.length > 0) {
      return `Say where a report carries the text's id and its status: ${missing.map((f) => FIELD_WORDS[f].label.toLowerCase()).join(", ")}.`;
    }
    if (!values.provider.trim()) return "Say whose reports these are, like twilio.";
    if (!PROVIDER_RE.test(values.provider.trim())) {
      return "The provider is a name in lowercase letters, digits, _ and -, up to 40.";
    }
    return null;
  }
  if (mapped("source_ref") && !values.lead_source.trim()) {
    return "A sender's id needs the source its ids belong to.";
  }
  if (values.action === MESSAGE_SEND) {
    if (!mapped("text")) return "Say where a request carries the words to send.";
    if (!mapped("conversation_id") && !IDENTIFIERS.some(mapped)) {
      return "Say who the text is to: where a request names the conversation to answer, or the lead.";
    }
    return null;
  }
  if (!IDENTIFIERS.some(mapped)) {
    return "Say where a request names the lead: its phone number, the sender's id, or our lead id.";
  }
  if (values.action === "lead.update" && Object.keys(values.fields).length === 0) {
    return "Choose at least one field of the contact's form to fill in.";
  }
  if (values.action === "lead.note" && !mapped("note")) return "Say where a request carries the note.";
  if (values.action === LEAD_OUTCOME) {
    if (values.outcomes.length === 0) return "Tick the outcomes this trigger may set.";
    if (values.outcomes.length > 1 && !mapped("outcome")) {
      return "With more than one outcome ticked, say where a request names which one it sets.";
    }
  }
  if (values.action === "lead.enlist") {
    if (values.campaign_id === EVERY_CAMPAIGN) return "Choose the campaign whose dial list the leads go on.";
    if (!mapped("phone")) return "Say where a request carries the phone number to call.";
    if (!mapped("source_ref")) {
      return "A new lead needs the sender's own id for them: say where a request carries it.";
    }
    return routingProblem(values.routing);
  }
  return null;
}

/** What `POST /triggers` takes: the fields and options the action reads, and nothing it does not.
 *  The secret's kind is said only when it is basic: a header is core-api's own default, and a
 *  core-api from before the kind could be chosen refuses a field it does not know. */
export function triggerPayload(
  values: TriggerValues,
): Required<Omit<TriggerInput, "tenant_id" | "secret_kind">> & { secret_kind?: string } {
  const mapping: Record<string, unknown> = {};
  for (const field of actionFields(values.action)) {
    const path = values.mapping[field].trim();
    if (path) mapping[field] = path;
  }
  if (values.action === "lead.enlist") {
    const attributes = Object.fromEntries(
      values.attributes
        .filter((row) => row.name.trim() || row.path.trim())
        .map((row) => [row.name.trim(), row.path.trim()]),
    );
    if (Object.keys(attributes).length > 0) mapping.attributes = attributes;
  }
  // The mapping is sent whole, so the form answers go with it: as the field mapper left them, or as
  // loaded when nobody touched it there.
  if (fillsFields(values.action) && Object.keys(values.fields).length > 0) mapping.fields = values.fields;
  const idempotency = values.idempotency.map((path) => path.trim()).filter(Boolean);
  if (idempotency.length > 0) mapping.idempotency = idempotency;

  const takes = ACTION_OPTIONS[values.action] ?? [];
  const options: Record<string, unknown> = {};
  if (takes.includes("hours") && values.hours.trim()) options.hours = Number(values.hours);
  for (const name of ["reason", "timezone", "consent_wording", "list_name"] as const) {
    if (takes.includes(name) && values[name].trim()) options[name] = values[name].trim();
  }
  // Only when it is off: true is the server's own default, and an option it never had is tidier
  // than one that says what would happen anyway.
  if (takes.includes("require_consent") && !values.require_consent) options.require_consent = false;
  if (takes.includes("provider") && values.provider.trim()) options.provider = values.provider.trim();
  // Only when it is not JSON, for the reason require_consent gives.
  if (takes.includes("reply") && values.reply === "twiml") options.reply = "twiml";
  if (takes.includes("outcomes") && values.outcomes.length > 0) options.outcomes = values.outcomes;
  if (takes.includes("firm_split")) Object.assign(options, routingOptions(values.routing));
  const basic = values.secret_kind === "basic";
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    action: values.action,
    mapping,
    options,
    // A text's inbox is its number's, a report is about one text, a text we are asked to send goes
    // to whoever the request names, and a write is on the lead's own record: none has a campaign.
    campaign_id: takesCampaign(values.action) && !writesRecord(values.action) ? values.campaign_id || null : null,
    lead_source: mapping.source_ref ? values.lead_source.trim() || null : null,
    ...(basic ? { secret_kind: "basic" } : {}),
    // Basic credentials travel in Authorization, which core-api sets itself and refuses as a
    // header's name: the default goes in its place and is not used.
    secret_header: basic ? DEFAULT_SECRET_HEADER : values.secret_header.trim(),
    enabled: values.enabled,
  };
}

/** JSON with every object's keys in order, to compare two mappings or options. */
function stable(value: unknown): string {
  return JSON.stringify(value, (_key, node: unknown) =>
    node && typeof node === "object" && !Array.isArray(node)
      ? Object.fromEntries(Object.entries(node).sort(([a], [b]) => a.localeCompare(b)))
      : node,
  );
}

/** The fields an edit changed, as `PUT /triggers/{id}` takes them. Mapping and options go whole. */
export function triggerChanges(values: TriggerValues, trigger: Trigger): TriggerUpdateInput {
  const next = triggerPayload(values);
  const out: TriggerUpdateInput = {};
  if (next.name !== trigger.name) out.name = next.name;
  if (next.description !== (trigger.description ?? null)) out.description = next.description;
  if (next.action !== trigger.action) out.action = next.action;
  if (stable(next.mapping) !== stable(trigger.mapping ?? {})) out.mapping = next.mapping;
  if (stable(next.options) !== stable(trigger.options ?? {})) out.options = next.options;
  if (next.campaign_id !== trigger.campaign_id) out.campaign_id = next.campaign_id;
  if (next.lead_source !== trigger.lead_source) out.lead_source = next.lead_source;
  const kind = next.secret_kind ?? "header";
  if (kind !== (trigger.secret_kind || "header")) out.secret_kind = kind;
  // Never a header for basic credentials: core-api names it, and refuses Authorization as one.
  if (kind === "header" && next.secret_header !== trigger.secret_header) {
    out.secret_header = next.secret_header;
  }
  if (next.enabled !== trigger.enabled) out.enabled = next.enabled;
  return out;
}

// --- the outcomes a trigger may set --------------------------------------------------------------

/** What picking an outcome lets a sender do to a lead, in a line: the stage it moves them to, what
 *  it does to the calls, and whether it needs a note. Do-not-call is said first, as the dialer reads
 *  it first. */
export function outcomeEffect(d: Pick<Disposition, "slug" | "kind" | "category" | "requires_note"> & { stage?: string | null }): string {
  const parts: string[] = [];
  if (marksDoNotCall(d)) parts.push("Marks the lead do-not-call");
  else {
    parts.push(d.stage ? `Moves the lead to ${stageText(d.stage).label}` : "Keeps the stage");
    parts.push(kindTerm(d.kind).label);
  }
  if (d.requires_note) parts.push("needs a note");
  return parts.join(" · ");
}

/** The ticked outcomes after one is ticked or unticked, kept in the catalog's order so that two
 *  admins who pick the same ones save the same list. One the catalog no longer has stays where it
 *  was, last: it is shown, and is the admin's to untick. */
export function toggleOutcome(picked: readonly string[], slug: string, catalog: readonly Pick<Disposition, "slug">[]): string[] {
  const next = picked.includes(slug) ? picked.filter((s) => s !== slug) : [...picked, slug];
  const order = new Map(catalog.map((d, index) => [d.slug, index]));
  return [...next].sort((a, b) => (order.get(a) ?? catalog.length) - (order.get(b) ?? catalog.length));
}

/** The ticked outcomes the catalog no longer offers: turned off, or deleted. core-api refuses a
 *  request that names one, and refuses the list when it is saved again. */
export function outcomesGone(picked: readonly string[], catalog: readonly Pick<Disposition, "slug">[]): string[] {
  const offered = new Set(catalog.map((d) => d.slug));
  return picked.filter((slug) => !offered.has(slug));
}

// --- what a sender sends -------------------------------------------------------------------------

export interface RequestPath {
  path: string;
  /** The request's value there. */
  example: string;
}

/**
 * Every path a mapping can name in a request the trigger received, with the value there: into
 * objects, and into the first items of a list. A boolean is left out, because a mapping never reads
 * one, and so is a key no path can spell.
 */
export function requestPaths(doc: unknown): RequestPath[] {
  const out: RequestPath[] = [];
  const visit = (node: unknown, path: string) => {
    if (typeof node === "string" || typeof node === "number") {
      if (path) out.push({ path, example: String(node) });
    } else if (Array.isArray(node)) {
      node.slice(0, 5).forEach((item, index) => visit(item, `${path}[${index}]`));
    } else if (node !== null && typeof node === "object") {
      for (const [key, value] of Object.entries(node)) visit(value, path ? `${path}.${key}` : key);
    }
  };
  visit(doc, "");
  return out.filter((entry) => PATH_RE.test(entry.path));
}

const EXAMPLES: Record<MappingField, string> = {
  phone: "+13105550123",
  source_ref: "contact-123",
  lead_id: "the lead's id",
  until: "2026-09-20T18:00:00Z",
  first_name: "Rosa",
  last_name: "Delgado",
  language: "es",
  state: "CA",
  timezone: "America/Los_Angeles",
  consent_at: "2026-09-13T16:05:00Z",
  tags: "lhpark_ca,spanish",
  firm_id: "3",
  from: "+13105550123",
  to: "+13105550100",
  text: "Can you call me back?",
  message_id: "SM0123456789abcdef",
  media_count: "0",
  status: "undelivered",
  error_code: "30007",
  note: "Agreement sent for signature.",
  outcome: "contract_sent",
  callback_at: "2026-09-20T15:00:00-05:00",
  conversation_id: "c9e04b17-8a35-4d62-b7f1-2e6a5c8d9f04",
};

/** What a send trigger's example says in the two fields it shares with a text that arrived. */
const SEND_EXAMPLES: Partial<Record<MappingField, string>> = {
  text: "Your appointment is tomorrow at 3 pm.",
  from: "+13105550100",
};

/** The outcome a sender is shown: one this trigger may really set, when it lists any. */
function exampleOutcome(trigger: { options?: Record<string, unknown> | null }): string {
  const listed = trigger.options?.outcomes;
  return Array.isArray(listed) && typeof listed[0] === "string" ? listed[0] : EXAMPLES.outcome;
}

/** A trigger's URL as another system can call it. `origin` is for an API address with no host of
 *  its own (`/api`, under the console's own origin). */
export function absoluteUrl(url: string, origin = ""): string {
  return /^https?:\/\//i.test(url) ? url : `${origin.replace(/\/$/, "")}${url}`;
}

/** A trigger's URL with basic credentials in it, as a sender that takes them there is given it. */
export function urlWithSecret(url: string, secret: string, origin = ""): string {
  return absoluteUrl(url, origin).replace(/^(https?:\/\/)/i, `$1${BASIC_USER}:${secret}@`);
}

/** One argument of a shell command, in single quotes. An apostrophe in it would end the quote (an
 *  example value like "the lead's id" does), so each one is closed over. */
export function shellQuote(text: string): string {
  return `'${text.replace(/'/g, `'\\''`)}'`;
}

/** What Twilio is told so that it sends a text again when we could not take it: by default it
 *  retries only a connection that failed. */
export const TWILIO_RETRY = "#rc=3&rp=5xx,ct,rt";

/** Writes `value` at `path`, making the objects and lists on the way. */
function place(doc: Record<string, unknown>, path: string, value: unknown): void {
  const steps = path.replace(/^\$\./, "").split(".");
  let node: Record<string, unknown> = doc;
  steps.forEach((step, i) => {
    const last = i === steps.length - 1;
    const indexed = /^([A-Za-z0-9_-]+)\[(\d{1,4})\]$/.exec(step);
    if (indexed) {
      const name = indexed[1] ?? step;
      const index = Number(indexed[2]);
      const list = Array.isArray(node[name]) ? (node[name] as unknown[]) : [];
      node[name] = list;
      // A body shows every slot before the one a path names: a hole would read as nothing there.
      while (list.length < index) list.push(null);
      if (last) {
        list[index] = value;
        return;
      }
      const held = list[index];
      const next = held && typeof held === "object" && !Array.isArray(held) ? (held as Record<string, unknown>) : {};
      list[index] = next;
      node = next;
    } else if (last) {
      node[step] = value;
    } else {
      const held = node[step];
      const next = held && typeof held === "object" && !Array.isArray(held) ? (held as Record<string, unknown>) : {};
      node[step] = next;
      node = next;
    }
  });
}

/** What "How to call it" reads of a trigger. The options are an outcome trigger's, for its example. */
type Called = Pick<Trigger, "action" | "mapping"> & Partial<Pick<Trigger, "options">>;

function mappedPaths(trigger: Called): [string, unknown][] {
  const mapping = trigger.mapping ?? {};
  const out: [string, unknown][] = [];
  for (const field of actionFields(trigger.action)) {
    const path = mapping[field];
    // An older trigger's consent mapping still works, and is not shown to a sender (ACL-575).
    if (field === "consent_at") continue;
    if (typeof path === "string" && path) {
      const example = field === "outcome"
        ? exampleOutcome(trigger)
        : ((trigger.action === MESSAGE_SEND ? SEND_EXAMPLES[field] : undefined) ?? EXAMPLES[field]);
      out.push([path, example]);
    }
  }
  const attributes = mapping.attributes;
  if (trigger.action === "lead.enlist" && attributes && typeof attributes === "object" && !Array.isArray(attributes)) {
    for (const [name, path] of Object.entries(attributes)) {
      if (typeof path === "string" && path) out.push([path, `the ${name}`]);
    }
  }
  if (Array.isArray(mapping.idempotency)) {
    mapping.idempotency.forEach((path, index) => {
      if (typeof path === "string" && path) out.push([path, `event-${index + 1}`]);
    });
  }
  return out;
}

/** The form answers an update's request carries, as the mapping finds them: by path, and by a
 *  top-level key's own name. A new lead's are left out of the example: they are most of a CRM's
 *  contact, and the sender already sends them. */
function updateAnswers(trigger: Pick<Trigger, "action" | "mapping">): { paths: [string, string][]; names: [string, string][] } {
  const out = { paths: [] as [string, string][], names: [] as [string, string][] };
  if (trigger.action !== "lead.update") return out;
  for (const [key, source] of Object.entries(fieldSources(trigger.mapping?.fields))) {
    if (typeof source === "string") out.paths.push([source, `the ${key}`]);
    else out.names.push([source.name, `the ${key}`]);
  }
  return out;
}

/** A body shaped the way the trigger's mapping reads one, with example values: what "How to call it"
 *  shows a sender. */
export function exampleBody(trigger: Called): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const answers = updateAnswers(trigger);
  for (const [path, value] of [...mappedPaths(trigger), ...answers.paths]) place(body, path, value);
  for (const [name, value] of answers.names) body[name] = value;
  return body;
}

/** The field names a form sends, when every path the trigger reads is a plain name; null when one
 *  steps into an object or a list, which only a JSON body carries. */
export function formFieldNames(trigger: Called): string[] | null {
  const answers = updateAnswers(trigger);
  const paths = [...mappedPaths(trigger), ...answers.paths].map(([path]) => path.replace(/^\$\./, ""));
  // A key found by its name is a form field of that name, whatever characters it has.
  const names = answers.names.map(([name]) => name);
  return paths.every((path) => PLAIN_NAME_RE.test(path)) ? [...new Set([...paths, ...names])] : null;
}
